import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { navbarVariant, socialIconHover } from '../../animations/animations';
import { MdMenu, MdClose, MdInsertDriveFile } from 'react-icons/md';
import { FaLinkedinIn, FaGithub } from 'react-icons/fa';
import ResumeModal from './ResumeModal';

const navLinks = [
  { name: 'About', href: '#about' },
  { name: 'Experience', href: '#experience' },
  { name: 'Projects', href: '#projects' },
  { name: 'Skills', href: '#skills' },
  { name: 'Contact', href: '#contact' },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isResumeOpen, setIsResumeOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href) => {
    setIsMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      const navbarHeight = 80;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementPosition - navbarHeight,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      <motion.nav
        variants={navbarVariant}
        initial="hidden"
        animate="visible"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-darkBg/90 backdrop-blur-lg border-b border-white/10 shadow-lg'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <motion.a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="text-2xl font-bold gradient-text hover:opacity-80 transition-opacity"
              whileHover={{ scale: 1.05 }}
            >
              SP
            </motion.a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => scrollToSection(link.href)}
                  className="text-textSecondary hover:text-primary transition-colors font-medium"
                >
                  {link.name}
                </button>
              ))}
            </div>

            {/* Desktop Actions */}
            <div className="hidden md:flex items-center gap-4">
              {/* Social Links */}
              <motion.a
                href="https://www.linkedin.com/in/siddu-suhas/"
                target="_blank"
                rel="noopener noreferrer"
                variants={socialIconHover}
                initial="rest"
                whileHover="hover"
                className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:border-blue-500 hover:bg-blue-500/10 transition-all"
              >
                <FaLinkedinIn className="w-4 h-4 text-textSecondary hover:text-blue-500" />
              </motion.a>

              <motion.a
                href="https://github.com/siddusuhaas"
                target="_blank"
                rel="noopener noreferrer"
                variants={socialIconHover}
                initial="rest"
                whileHover="hover"
                className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:border-gray-400 hover:bg-gray-500/10 transition-all"
              >
                <FaGithub className="w-4 h-4 text-textSecondary hover:text-gray-300" />
              </motion.a>

              {/* Resume Button */}
              <button
                onClick={() => setIsResumeOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primaryDark text-white rounded-lg font-medium transition-all"
              >
                <MdInsertDriveFile className="w-4 h-4" />
                Resume
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center"
            >
              {isMobileMenuOpen ? (
                <MdClose className="w-6 h-6 text-textPrimary" />
              ) : (
                <MdMenu className="w-6 h-6 text-textPrimary" />
              )}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 md:hidden"
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Menu Content */}
            <div className="absolute top-20 left-0 right-0 bg-darkSurface border-b border-white/10 p-6">
              <div className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  <button
                    key={link.name}
                    onClick={() => scrollToSection(link.href)}
                    className="text-left text-lg text-textSecondary hover:text-primary transition-colors py-2"
                  >
                    {link.name}
                  </button>
                ))}

                <div className="border-t border-white/10 pt-4 mt-2">
                  <div className="flex gap-4 mb-4">
                    <a
                      href="https://www.linkedin.com/in/siddu-suhas/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:border-blue-500 hover:bg-blue-500/10 transition-all"
                    >
                      <FaLinkedinIn className="w-5 h-5 text-textSecondary" />
                    </a>
                    <a
                      href="https://github.com/siddusuhaas"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:border-gray-400 hover:bg-gray-500/10 transition-all"
                    >
                      <FaGithub className="w-5 h-5 text-textSecondary" />
                    </a>
                  </div>

                  <button
                    onClick={() => { setIsMobileMenuOpen(false); setIsResumeOpen(true); }}
                    className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary hover:bg-primaryDark text-white rounded-lg font-medium transition-all"
                  >
                    <MdInsertDriveFile className="w-5 h-5" />
                    View Resume
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <ResumeModal isOpen={isResumeOpen} onClose={() => setIsResumeOpen(false)} />
    </>
  );
};

export default Navbar;
